import authService from "components/Auth/api-authorization/AuthorizeService";
import { ImpersonationState } from "models/ImpersonationState";
import { fetch_retry } from "utils/FetchRetry";

// Error thrown when an Impersonation API call fails. Carries the stable backend
// error code (from the ErrorModel `otherDetails` field) so callers can show a
// controlled message rather than raw server text. (AUT-10623)
export class ImpersonationRequestError extends Error {
    readonly status: number;
    readonly code?: string;
    constructor(message: string, status: number, code?: string) {
        super(message);
        this.name = "ImpersonationRequestError";
        this.status = status;
        this.code = code;
    }
}

// Controlled, user-friendly messages keyed off the backend error code. The
// message text is never taken from the server response. (AUT-10623)
const START_ERROR_MESSAGES: Record<string, string> = {
    CannotImpersonateUltra:
        "You don't have permission to impersonate this user.",
    TargetOrgNotAuthorized:
        "You don't have access to impersonate a user in that organization.",
    TargetDisabled: "This user is disabled and can't be impersonated.",
};

export function getImpersonationStartErrorMessage(code?: string): string {
    return (
        (code && Object.prototype.hasOwnProperty.call(START_ERROR_MESSAGES, code)
            ? START_ERROR_MESSAGES[code] : undefined) ||
        "We couldn't start impersonation. Please try again."
    );
}

export default class ImpersonationService {
    private readonly urlBase = "/api/Impersonation";

    private async getHeaders(orgIdOverride?: string) {
        const user = await authService.getUserObject();
        const token = user?.access_token;
        const orgId = orgIdOverride ?? user?.profile?.OrgId;

        return {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(orgId ? { "Acx-OrganizationId": orgId } : {}),
            "Acx-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
    }

    private async request<T>(
        method: "GET" | "POST",
        path: string,
        body?: unknown,
        orgIdOverride?: string,
    ): Promise<T> {
        const response = await fetch_retry(
            `${this.urlBase}/${path}`,
            {
                method,
                headers: await this.getHeaders(orgIdOverride),
                ...(body === undefined ? {} : { body: JSON.stringify(body) }),
            },
            method === "GET" ? 2 : 1,
        );

        if (!response.ok) {
            // Parse the ErrorModel body defensively: it may be empty or non-JSON.
            // On anything unexpected we throw with no code, and callers fall back
            // to a generic message. (AUT-10623)
            const raw = await response.text().catch(() => "");
            let code: string | undefined;
            let serverMessage: string | undefined;
            if (raw) {
                try {
                    const parsed = JSON.parse(raw);
                    code = typeof parsed?.otherDetails === "string" ? parsed.otherDetails : undefined;
                    serverMessage = parsed?.message;
                } catch {
                    // Non-JSON body — leave code/message undefined.
                }
            }
            throw new ImpersonationRequestError(
                serverMessage || raw || "Request failed",
                response.status,
                code,
            );
        }

        return response.json();
    }

    public async getCurrent(orgIdOverride?: string): Promise<ImpersonationState> {
        return this.request("GET", "Current", undefined, orgIdOverride);
    }

    public async start(
        targetUserId: string,
        reason?: string,
        orgIdOverride?: string,
    ): Promise<ImpersonationState> {
        return this.request("POST", "Start", { targetUserId, reason }, orgIdOverride);
    }

    public async stop(orgIdOverride?: string): Promise<ImpersonationState> {
        return this.request("POST", "Stop", undefined, orgIdOverride);
    }
}
